/**
 * @file Provides uploading capability in the frontend.
 *
 * Will try resumable uploads (direct to Google Cloud Storage) first, then
 * fallback to Beethoven uploads.
 *
 */
import { trackEvent } from '@@/bits/analytics'
import { customAjax, type CustomXHR } from '@@/bits/custom_ajax'
import { captureMessage } from '@@/bits/error_tracker'
import { getEmbeddedUrl, getName } from '@@/bits/file_handler'
import type { GCSResumableUpload } from '@@/bits/gcs_resumable_upload'
import {
  cancelResumableUpload,
  createResumableUpload,
  INVALID_RESUMABLE_SESSION_URI,
  startResumableUpload,
  subscribeUploadEvent,
  supportsGcsResumableUploads,
} from '@@/bits/gcs_resumable_upload'
import { defer } from 'es-toolkit/compat'
import EventEmitter from 'events'

let uploadsInProgress: UploadJob[] = []
const FILE_TOO_LARGE_ERROR = 'FILE_TOO_LARGE_ERROR'
const ONE_MB = 1024 * 1024
const MAX_FILE_UPLOAD_BYTES_SIZE = 31457280

const trackError = function (label: string, context: Record<string, any> = {}): void {
  trackEvent('Uploads', 'Upload failed', label)
  context.fileType = label
  captureMessage('UploadError', { context })
}

const trackSuccess = function (label: string): void {
  trackEvent('Uploads', 'Upload succeeded', label)
}

interface UploadOptions {
  maxFileSize?: number
  trimVideoAfterUpload?: {
    start: number
    end: number
    trimToken: string
    outputMimeType?: string
  }
}

class UploadJob extends EventEmitter {
  file: File
  options: UploadOptions

  private xhr1?: CustomXHR
  private xhr2?: CustomXHR
  private completionXhr?: CustomXHR
  private trimXhr?: CustomXHR
  private resumableUpload?: GCSResumableUpload

  constructor(file, options = {}) {
    super()
    this.file = file
    this.options = options
  }

  maxFileSize() {
    if (this.options.maxFileSize) {
      // We give 1MB extra. Psychologically, 25.5MB is 25MB, not 26MB.
      return this.options.maxFileSize + ONE_MB
    }
    return this.options.maxFileSize
  }

  cancel() {
    if (this.xhr1 != null) this.xhr1.abort()
    if (this.xhr2 != null) this.xhr2.abort()
    if (this.resumableUpload != null) cancelResumableUpload(this.resumableUpload)
    if (this.completionXhr != null) this.completionXhr.abort()
    if (this.trimXhr != null) this.trimXhr.abort()
    this.emit('cancel')
  }

  perform(): void {
    const maxFileSizeBytes = this.maxFileSize()
    if (maxFileSizeBytes != null && this.file.size > maxFileSizeBytes) {
      trackEvent('Uploads', 'Upload failed due to big filesize', this.fileType, this.file.size)
      this._reject({ error: FILE_TOO_LARGE_ERROR })
    } else {
      // Sometimes, a file can just be a holder of a url. (e.g. gdoc file)
      // It so, try to get the URL and just return that.
      getEmbeddedUrl(this.file).then(this._resolve.bind(this), this._ajax1.bind(this, this._useResumable()))
    }
  }

  _progress(e, timeStart) {
    const loadRatio = e.loaded / e.total
    // const timeNow = new Date()
    // const timeElapsed = timeNow - timeStart
    // const expectedTimeToProcess = 3000 //3 seconds
    // const loadRatio = e.loaded / e.total
    // const expectedTimeToUpload = timeElapsed / loadRatio
    // const expectedTimeToUploadAndProcess = expectedTimeToUpload + expectedTimeToProcess;
    // const actualProgress = timeElapsed/expectedTimeToUploadAndProcess
    this.emit('progress', loadRatio)
  }

  _useResumable() {
    return supportsGcsResumableUploads()
  }

  _resolve(obj) {
    this.emit('done', obj)
  }

  _reject(obj) {
    this.emit('error', obj)
  }

  // We will request to do resumable uploads but the backend
  // can override that decision.
  _ajax1(requestResumableEndpoint = false) {
    const self = this
    this.xhr1 = customAjax({
      url: '/media/upload_attributes.json',
      dataType: 'json',
      type: 'GET',
      cache: false,
      sendOauthToken: true,
      data: {
        unique: +new Date(),
        fileSize: self.file.size,
        filename: self.file.name,
        contentType: self.fileType,
        resumable: requestResumableEndpoint,
        maxFileSize: this.maxFileSize(),
      },
      error(responseData, textStatus) {
        if (textStatus === 'abort') return
        trackError('ajax1', {
          statusCode: responseData.status,
          readyState: responseData.readyState,
        })
        self._reject(responseData)
      },
      success(attributes) {
        const { resumable } = attributes
        if (resumable) {
          self._resumable(attributes)
        } else {
          self._ajax2(attributes)
        }
      },
    })
  }

  _ajax2(attributes) {
    const timeStart = new Date()
    const self = this
    const formData = new FormData()
    const name = getName(this.file)
    const originalFilename = this.file.name ? this.file.name : name

    const fieldsOverrides: Record<string, any> = {}
    if (this.options.maxFileSize) {
      fieldsOverrides.max_file_size = this.options.maxFileSize + 1048576
      fieldsOverrides.transcode = true
    }
    const fields = Object.assign({}, attributes.fields, fieldsOverrides)
    Object.entries(fields).forEach((x) => {
      if (x[0] !== 'key') formData.append(x[0], x[1] as string)
    })
    formData.append('key', [attributes.fields.key, name].join('/'))
    formData.append('Content-Type', this.fileType)
    formData.append('file_size', String(this.file.size))
    formData.append('title', originalFilename)
    formData.append('file', this.file, name)

    this.xhr2 = customAjax({
      url: attributes.url,
      crossDomain: true,
      type: 'POST',
      data: formData,
      cache: false,
      contentType: false,
      processData: false,
      dataType: 'json',
      xhr() {
        const xhr = new XMLHttpRequest()
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            self._progress(e, timeStart)
          }
        })
        return xhr
      },
      error(responseData, textStatus) {
        if (textStatus === 'abort') return
        trackError(self.fileType, {
          statusCode: responseData.status,
          userId: fields.uploader_id,
          filename: self.file.name,
          filsSize: self.file.size,
          maxFileSize: fields.max_file_size ?? 'None',
          resuamble: false,
          readyState: responseData.readyState,
          responseText: responseData.responseText,
          responseXml: responseData.responseXML,
        })
        self._reject(responseData)
      },
      success(responseData) {
        trackSuccess(self.fileType)
        self._resolve(responseData)
      },
    })
  }

  _resumable(attributes) {
    const timeStart = new Date()
    const self = this
    const file = self.file
    const { fields } = attributes
    const { completion_url: completionUrl } = fields

    // Standard way of handling error and falling back
    const trackErrorAndFallback = (error: Error): void => {
      trackError(self.fileType, {
        action: 'gcsResumableUpload',
        message: error.message,
        type: 'GCS Resumable Upload',
        fileSize: file.size,
        filename: file.name,
        userId: fields.uploader_id,
        maxFileSize: fields.max_file_size || 'None',
      })

      // Fallback to the older way
      self._ajax1(false)
    }

    // 1. Check that resumable URL is successfully created
    const sanityCheck = (gru) => {
      const { resumableSessionUri } = gru
      // Check if we initiated the session successfully
      if (!resumableSessionUri || resumableSessionUri === INVALID_RESUMABLE_SESSION_URI) {
        throw new Error('Cannot perform resumable upload')
      }

      // Assign for later user e.g. when cancelling the upload
      self.resumableUpload = gru
      return gru
    }

    // 2. Subscribe to events. Know when things fail, when there's progress, when an
    // upload is complete.
    const subscribeToUploadEvents = (gru) => {
      subscribeUploadEvent(gru, 'error', (gru, error) => {
        trackErrorAndFallback(error)
      })

      subscribeUploadEvent(gru, 'progress', (gru, fractionPercentage) => {
        if (self.isVideoFile && self.options.trimVideoAfterUpload != null) {
          // If we're going to trim, scale upload progress to only 50%.
          // We allocate the other 50% for the trim operation.
          self._progress({ loaded: fractionPercentage * 0.5, total: 1.0 }, timeStart)
        } else {
          // Normal progress reporting for non-video trimming uploads
          self._progress({ loaded: fractionPercentage, total: 1.0 }, timeStart)
        }
      })

      subscribeUploadEvent(gru, 'completion', async () => {
        if (self.shouldTrimVideoAfterUpload && self.options.trimVideoAfterUpload != null) {
          // If we need to trim the video, we do so after the upload is complete.
          // We simulate the progress of the trim operation to give the user feedback.
          const { timer, cleanup } = self._simulateTrimProgress(timeStart)

          try {
            // TODO: make this async!
            const trimResult: any = await self._trimVideoAfterUpload(fields.uploaded_url, {
              start: self.options.trimVideoAfterUpload.start,
              end: self.options.trimVideoAfterUpload.end,
              trimToken: fields.trim_token,
            })

            cleanup()

            // Video is trimmed and transcoded so url or extension may differ from original upload
            fields.uploaded_url = trimResult.url
            fields.filename = trimResult.filename
          } catch (error) {
            clearInterval(timer)
            throw error
          }
        }
        self._completeResumable(completionUrl, fields)
      })
      return gru
    }

    createResumableUpload(file, attributes)
      .then(sanityCheck)
      .then(subscribeToUploadEvents)
      .then(startResumableUpload)
      .catch(trackErrorAndFallback)
  }

  _completeResumable(completionUrl, fields) {
    const self = this
    const file = self.file
    this.completionXhr = customAjax({
      url: completionUrl,
      crossDomain: true,
      dataType: 'json',
      type: 'POST',
      cache: false,
      data: {
        completeResumable: true,
        ...fields,
      },
      error(responseData, textStatus) {
        if (textStatus === 'abort') return
        trackError(self.fileType, {
          action: 'completeResumable',
          userId: fields.uploader_id,
          filename: file.name,
          maxFileSize: fields.max_file_size || 'None',
          fileSize: file.size,
          statusCode: responseData.status,
          readyState: responseData.readyState,
        })
        self._ajax1(false)
      },
      success(responseData) {
        self._resolve(responseData)
        trackSuccess(self.fileType)
      },
    })
  }

  get fileType(): string {
    if (this.file == null) {
      trackError('missing_file', {
        context: {
          uploadOptions: this.options,
          stack: new Error().stack,
        },
      })
      return 'application/octet-stream'
    }
    return this.file.type ?? 'application/octet-stream'
  }

  // #region Video trimming
  async _trimVideoAfterUpload(uploadedUrl, trimOptions) {
    return await new Promise((resolve, reject) => {
      this.trimXhr = customAjax({
        url: `/beethoven-v2/video/trim`,
        dataType: 'json',
        type: 'POST',
        headers: {
          Authorization: `Bearer ${trimOptions.trimToken}`,
        },
        data: {
          url: uploadedUrl,
          start: trimOptions.start,
          end: trimOptions.end,
          outputMimeType: trimOptions.outputMimeType,
        },
        cache: false,
        timeout: 5 * 60 * 1000, // 5 minutes
        error(responseData) {
          reject(responseData)
        },
        success(responseData) {
          resolve(responseData)
        },
      })
    })
  }

  _simulateTrimProgress(timeStart: Date): { timer: ReturnType<typeof setInterval>; cleanup: () => void } {
    let trimProgress = 0
    const BASE_FILE_SIZE = 25 * 1024 * 1024 // 25MB, roughly corresponds to 1m of video
    const BASE_DURATION = 30000 // 30 seconds base duration
    const DURATION_STEP = 10000 // 10 second increase per size tier
    const SIZE_MULTIPLIERS = [2, 3, 5, 8, 13, 21] // Fibonacci-like sequence. Values from experimentation roughly follows this.

    // Find the appropriate duration based on file size
    const fileSize = this.file.size
    let expectedTrimDuration = BASE_DURATION

    for (let i = 0; i < SIZE_MULTIPLIERS.length; i++) {
      if (fileSize > BASE_FILE_SIZE * SIZE_MULTIPLIERS[i]) {
        expectedTrimDuration += DURATION_STEP
      } else {
        break
      }
    }
    const progressInterval = 500 // Update every 500ms
    const progressIncrement = 0.5 / (expectedTrimDuration / progressInterval)

    const progressTimer = setInterval(() => {
      trimProgress += progressIncrement
      // Ensure we don't exceed 100% (50% upload + 50% trim)
      const totalProgress = Math.min(0.5 + trimProgress, 1.0)
      this._progress({ loaded: totalProgress, total: 1.0 }, timeStart)
    }, progressInterval)

    return {
      timer: progressTimer,
      cleanup: () => {
        clearInterval(progressTimer)
        // Ensure we show 100% completion
        this._progress({ loaded: 1.0, total: 1.0 }, timeStart)
      },
    }
  }

  get isVideoFile(): boolean {
    return this.fileType.startsWith('video/')
  }

  get shouldTrimVideoAfterUpload(): boolean {
    return this.isVideoFile && this.options.trimVideoAfterUpload != null
  }
  // #endregion
}

const startUpload = function (file, options = {}) {
  const uploadJob = new UploadJob(file, options)
  uploadJob.on('done', (result) => {
    uploadsInProgress = uploadsInProgress.filter((x) => x != uploadJob)
  })
  uploadJob.on('error', (error) => {
    uploadsInProgress = uploadsInProgress.filter((x) => x != uploadJob)
  })
  uploadJob.on('cancel', (error) => {
    uploadsInProgress = uploadsInProgress.filter((x) => x != uploadJob)
  })
  uploadsInProgress.push(uploadJob)
  defer(() => uploadJob.perform())
  return uploadJob
}

const stopUpload = function (file) {
  const uploadJob = uploadsInProgress.find((x) => x.file == file)
  if (uploadJob != null) {
    uploadJob.cancel()
  }
}

export { FILE_TOO_LARGE_ERROR, MAX_FILE_UPLOAD_BYTES_SIZE, startUpload, stopUpload, UploadJob }
